import { supabase } from '@/integrations/supabase/client';
import type { Database, Json } from '@/integrations/supabase/types';

export type ClientePotencial = Database['public']['Tables']['clientes_potenciais']['Row'];
export type ClienteInsert = Database['public']['Tables']['clientes_potenciais']['Insert'];
export type ClienteUpdate = Database['public']['Tables']['clientes_potenciais']['Update'];
export type StatusCliente = Database['public']['Enums']['status_cliente'];
export type TipoEventoCliente = Database['public']['Enums']['tipo_evento'];

export async function listClientes(mostrarArquivados: boolean): Promise<unknown[]> {
  let query = supabase
    .from('clientes_potenciais')
    .select(`
      *,
      responsavel:profiles!clientes_potenciais_responsavel_id_fkey(id, nome, avatar_url),
      tags:tags_clientes(tag:tags(id, nome))
    `)
    .order('ultima_interacao', { ascending: false });

  if (!mostrarArquivados) {
    query = query.eq('arquivado', false);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function getClienteById(id: string): Promise<unknown> {
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .select(`
      *,
      responsavel:profiles!clientes_potenciais_responsavel_id_fkey(id, nome, avatar_url),
      tags:tags_clientes(tag:tags(id, nome))
    `)
    .eq('id', id)
    .single();
  if (error) throw error;
  return data;
}

export interface RegistrarEventoClienteInput {
  clienteId: string;
  orgId: string;
  profileId?: string;
  tipo: TipoEventoCliente;
  descricao: string;
  detalhes?: Record<string, unknown>;
}

export async function registrarEventoCliente(input: RegistrarEventoClienteInput): Promise<void> {
  const { error } = await supabase.from('eventos').insert({
    cliente_potencial_id: input.clienteId,
    usuario_id: input.profileId,
    tipo: input.tipo,
    descricao: input.descricao,
    detalhes: (input.detalhes ?? {}) as unknown as Json,
    organization_id: input.orgId,
  });
  if (error) throw error;
}

export async function atualizarCliente(id: string, dados: ClienteUpdate): Promise<ClientePotencial> {
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .update(dados)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export interface EditarClienteInput {
  id: string;
  orgId: string;
  dados: ClienteUpdate;
  camposAlterados: string[];
  profileId?: string;
}

export async function editarCliente(input: EditarClienteInput): Promise<ClientePotencial> {
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .update({ ...input.dados, ultima_interacao: new Date().toISOString() })
    .eq('id', input.id)
    .select()
    .single();
  if (error) throw error;

  await registrarEventoCliente({
    clienteId: input.id,
    orgId: input.orgId,
    profileId: input.profileId,
    tipo: 'edicao_dados_cliente',
    descricao: 'Dados do cliente atualizados',
    detalhes: { campos_alterados: input.camposAlterados },
  });

  return data;
}

export interface AtualizarStatusClienteInput {
  id: string;
  orgId: string;
  status: StatusCliente;
  statusLabel: string;
}

export async function atualizarStatusCliente(input: AtualizarStatusClienteInput): Promise<ClientePotencial & { responsavel?: unknown }> {
  // Arquivamento é exclusivamente manual (ver arquivarCliente); mudar status nunca arquiva.
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .update({ status: input.status, ultima_interacao: new Date().toISOString() })
    .eq('id', input.id)
    .select(`*, responsavel:profiles!clientes_potenciais_responsavel_id_fkey(id, nome)`)
    .single();
  if (error) throw error;

  await registrarEventoCliente({
    clienteId: input.id,
    orgId: input.orgId,
    tipo: 'status_alterado',
    descricao: `Status alterado para ${input.statusLabel}`,
    detalhes: { status_novo: input.status },
  });

  return data as ClientePotencial & { responsavel?: unknown };
}

export interface CriarClienteInput {
  dados: ClienteInsert;
  orgId: string;
}

export async function criarCliente(input: CriarClienteInput): Promise<ClientePotencial> {
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .insert({ ...input.dados, organization_id: input.orgId })
    .select()
    .single();
  if (error) throw error;

  await registrarEventoCliente({
    clienteId: data.id,
    orgId: input.orgId,
    tipo: 'cliente_criado',
    descricao: `Cliente potencial criado (origem: ${input.dados.origem || 'Manual'})`,
  });

  return data;
}

export class ClienteJaAtribuidoError extends Error {
  constructor() {
    super('Este cliente já possui um responsável.');
    this.name = 'ClienteJaAtribuidoError';
  }
}

export interface AssumirAtendimentoInput {
  clienteId: string;
  orgId: string;
  profileId: string;
  profileNome: string;
}

export async function assumirAtendimento(input: AssumirAtendimentoInput): Promise<ClientePotencial> {
  const { data: cliente, error: fetchError } = await supabase
    .from('clientes_potenciais')
    .select('responsavel_id, status')
    .eq('id', input.clienteId)
    .single();
  if (fetchError) throw fetchError;

  // Fast-path: evita a escrita no caso comum. A guarda real é o .is(...) abaixo.
  if (cliente?.responsavel_id) {
    throw new ClienteJaAtribuidoError();
  }

  const novoStatus: StatusCliente = cliente?.status === 'novo' || cliente?.status === 'aguardando_atendente'
    ? 'em_qualificacao'
    : cliente!.status;

  // UPDATE condicional: só atualiza se responsavel_id ainda for null. Atómico no
  // Postgres — fecha a race entre dois atendentes a assumir o mesmo cliente.
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .update({
      responsavel_id: input.profileId,
      status: novoStatus,
      ultima_interacao: new Date().toISOString(),
    })
    .eq('id', input.clienteId)
    .is('responsavel_id', null)
    .select()
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new ClienteJaAtribuidoError(); // 0 linhas → alguém assumiu primeiro

  await registrarEventoCliente({
    clienteId: input.clienteId,
    orgId: input.orgId,
    profileId: input.profileId,
    tipo: 'atendimento_iniciado',
    descricao: `Atendimento assumido por ${input.profileNome}`,
    detalhes: { atendente_id: input.profileId, atendente_nome: input.profileNome },
  });

  return data;
}

export interface ExcluirClienteInput {
  clienteId: string;
  orgId: string;
  clienteNome?: string;
  profileId?: string;
  profileNome?: string;
}

export async function excluirCliente(input: ExcluirClienteInput): Promise<{ clienteId: string }> {
  if (input.profileId) {
    await registrarEventoCliente({
      clienteId: input.clienteId,
      orgId: input.orgId,
      profileId: input.profileId,
      tipo: 'cliente_excluido',
      descricao: `Cliente "${input.clienteNome || 'Sem nome'}" excluído por ${input.profileNome || 'Usuário'}`,
      detalhes: {
        excluido_por_id: input.profileId,
        excluido_por_nome: input.profileNome,
        cliente_nome: input.clienteNome,
        data_exclusao: new Date().toISOString(),
      },
    });
  }

  const { error } = await supabase
    .from('clientes_potenciais')
    .delete()
    .eq('id', input.clienteId);
  if (error) throw error;

  return { clienteId: input.clienteId };
}

export interface ArquivarClienteInput {
  clienteId: string;
  orgId: string;
  clienteNome?: string;
  profileId?: string;
  profileNome?: string;
}

export async function arquivarCliente(input: ArquivarClienteInput): Promise<ClientePotencial> {
  const { data, error } = await supabase
    .from('clientes_potenciais')
    .update({
      arquivado: true,
      ultima_interacao: new Date().toISOString(),
    })
    .eq('id', input.clienteId)
    .select()
    .single();
  if (error) throw error;

  if (input.profileId) {
    await registrarEventoCliente({
      clienteId: input.clienteId,
      orgId: input.orgId,
      profileId: input.profileId,
      tipo: 'cliente_arquivado',
      descricao: `Cliente "${input.clienteNome || 'Sem nome'}" arquivado por ${input.profileNome}`,
      detalhes: {
        arquivado_por_id: input.profileId,
        arquivado_por_nome: input.profileNome,
      },
    });
  }

  return data;
}

export interface DesarquivarClienteInput {
  clienteId: string;
  orgId: string;
  clienteNome?: string;
  novoStatus?: StatusCliente;
  profileId?: string;
  profileNome?: string;
}

export async function desarquivarCliente(input: DesarquivarClienteInput): Promise<ClientePotencial> {
  const statusRetorno = input.novoStatus || 'voltar_contato';

  const { data, error } = await supabase
    .from('clientes_potenciais')
    .update({
      arquivado: false,
      status: statusRetorno,
      ultima_interacao: new Date().toISOString(),
    })
    .eq('id', input.clienteId)
    .select()
    .single();
  if (error) throw error;

  if (input.profileId) {
    await registrarEventoCliente({
      clienteId: input.clienteId,
      orgId: input.orgId,
      profileId: input.profileId,
      tipo: 'cliente_desarquivado',
      descricao: `Cliente "${input.clienteNome || 'Sem nome'}" reativado por ${input.profileNome}`,
      detalhes: {
        reativado_por_id: input.profileId,
        reativado_por_nome: input.profileNome,
        novo_status: statusRetorno,
      },
    });
  }

  return data;
}

export interface TransferirAtendimentoInput {
  clienteId: string;
  orgId: string;
  novoResponsavelId: string;
  novoResponsavelNome: string;
  responsavelAnteriorId?: string | null;
  responsavelAnteriorNome?: string | null;
  profileId: string;
  profileNome: string;
}

export async function transferirAtendimento(input: TransferirAtendimentoInput): Promise<ClientePotencial & { responsavel?: unknown }> {
  const novoStatus: StatusCliente | undefined = !input.responsavelAnteriorId ? 'em_qualificacao' : undefined;

  const updateData: ClienteUpdate = novoStatus
    ? {
        responsavel_id: input.novoResponsavelId,
        status: novoStatus,
        ultima_interacao: new Date().toISOString(),
      }
    : {
        responsavel_id: input.novoResponsavelId,
        ultima_interacao: new Date().toISOString(),
      };

  const { data, error } = await supabase
    .from('clientes_potenciais')
    .update(updateData)
    .eq('id', input.clienteId)
    .select(`*, responsavel:profiles!clientes_potenciais_responsavel_id_fkey(id, nome)`)
    .single();
  if (error) throw error;

  await registrarEventoCliente({
    clienteId: input.clienteId,
    orgId: input.orgId,
    profileId: input.profileId,
    tipo: 'atendimento_transferido',
    descricao: `Atendimento transferido de "${input.responsavelAnteriorNome || 'Não atribuído'}" para "${input.novoResponsavelNome}"`,
    detalhes: {
      transferido_por_id: input.profileId,
      transferido_por_nome: input.profileNome,
      responsavel_anterior_id: input.responsavelAnteriorId,
      responsavel_anterior_nome: input.responsavelAnteriorNome,
      novo_responsavel_id: input.novoResponsavelId,
      novo_responsavel_nome: input.novoResponsavelNome,
    },
  });

  return data as ClientePotencial & { responsavel?: unknown };
}
